import axios, { AxiosError } from "axios";

export type AdminCopilotErrorCode = "FORBIDDEN" | "NOT_FOUND" | "NETWORK" | "UNKNOWN";

export class AdminCopilotError extends Error {
  readonly code: AdminCopilotErrorCode;
  readonly status?: number;

  constructor(message: string, code: AdminCopilotErrorCode, status?: number) {
    super(message);
    this.name = "AdminCopilotError";
    this.code = code;
    this.status = status;
  }
}

export interface AdminCopilotMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
}

export interface AdminCopilotSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

function agentApiBaseUrl(): string {
  return (import.meta.env.VITE_AGENT_API_BASE_URL || "http://localhost:4100/api").replace(/\/$/, "");
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function client(timeout = 45_000) {
  return axios.create({
    baseURL: agentApiBaseUrl(),
    timeout,
  });
}

function unwrap<T>(payload: { data?: T } | T): T {
  let current: unknown = payload;
  for (let depth = 0; depth < 3; depth += 1) {
    if (!current || typeof current !== "object" || Array.isArray(current)) break;
    const row = current as Record<string, unknown>;
    if ("conversationId" in row || "text" in row || "id" in row || "messages" in row) {
      return current as T;
    }
    if (row.data !== undefined) {
      current = row.data;
      continue;
    }
    break;
  }
  return current as T;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function conversationIdFrom(value: unknown): string | undefined {
  const id = String(asRecord(value).conversationId ?? "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)
    ? id
    : undefined;
}

function errorFromAxios(error: unknown): AdminCopilotError {
  if (error instanceof AdminCopilotError) return error;
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const status = axiosError.response?.status;
    if (status === 403) return new AdminCopilotError("FORBIDDEN", "FORBIDDEN", status);
    if (status === 401) return new AdminCopilotError("FORBIDDEN", "FORBIDDEN", status);
    if (status === 404) return new AdminCopilotError("NOT_FOUND", "NOT_FOUND", status);
    if (!axiosError.response) return new AdminCopilotError("NETWORK", "NETWORK");
  }
  return new AdminCopilotError("UNKNOWN", "UNKNOWN");
}

export async function sendAdminCopilotMessage(input: {
  tenantId: string;
  message: string;
  locale: "en" | "ar";
  conversationId?: string;
  route?: string;
  signal?: AbortSignal;
}): Promise<{ conversationId: string; text: string }> {
  try {
    const response = await client().post(
      "/v1/admin/chat",
      {
        tenantId: input.tenantId,
        message: input.message,
        locale: input.locale,
        ...(conversationIdFrom({ conversationId: input.conversationId }) && {
          conversationId: input.conversationId,
        }),
        route: input.route,
      },
      { signal: input.signal, headers: authHeader() },
    );
    const payload = unwrap<Record<string, unknown>>(response.data);
    return {
      conversationId: conversationIdFrom(payload) ?? "",
      text: String(payload.text ?? ""),
    };
  } catch (error) {
    throw errorFromAxios(error);
  }
}

export async function listAdminCopilotConversations(input: {
  tenantId: string;
  locale: "en" | "ar";
  signal?: AbortSignal;
}): Promise<AdminCopilotSummary[]> {
  try {
    const response = await client(15_000).get("/v1/admin/conversations", {
      params: { tenantId: input.tenantId, locale: input.locale },
      signal: input.signal,
      headers: authHeader(),
    });
    const payload = unwrap<unknown>(response.data);
    return Array.isArray(payload) ? (payload as AdminCopilotSummary[]) : [];
  } catch (error) {
    throw errorFromAxios(error);
  }
}

export async function getAdminCopilotConversation(input: {
  tenantId: string;
  id: string;
  locale: "en" | "ar";
}): Promise<{ id: string; messages: AdminCopilotMessage[] }> {
  try {
    const response = await client(15_000).get(`/v1/admin/conversations/${input.id}`, {
      params: { tenantId: input.tenantId, locale: input.locale },
      headers: authHeader(),
    });
    const payload = unwrap<Record<string, unknown>>(response.data);
    const messages: AdminCopilotMessage[] = Array.isArray(payload.messages)
      ? payload.messages.flatMap((item): AdminCopilotMessage[] => {
          if (!item || typeof item !== "object") return [];
          const row = item as Record<string, unknown>;
          const id = String(row.id ?? "");
          const role = row.role === "user" || row.role === "assistant" ? row.role : null;
          if (!id || !role) return [];
          return [{ id, role, text: String(row.content ?? row.text ?? "") }];
        })
      : [];
    return { id: String(payload.id ?? input.id), messages };
  } catch (error) {
    throw errorFromAxios(error);
  }
}

export async function deleteAdminCopilotConversation(input: {
  tenantId: string;
  id: string;
}): Promise<void> {
  try {
    await client(15_000).delete(`/v1/admin/conversations/${input.id}`, {
      params: { tenantId: input.tenantId },
      headers: authHeader(),
    });
  } catch (error) {
    throw errorFromAxios(error);
  }
}
